import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

import { tokenStore } from './api';

/**
 * The WebSocket, and the decision to use it.
 *
 * ── WHY THERE IS STILL A FALLBACK ───────────────────────────────────────────
 *
 * Reverb is a daemon. Daemons restart on deploy, die under memory pressure and
 * are unreachable from a phone that has just dropped to 3G in a lift. A chat
 * that goes silent in any of those cases has not degraded, it has broken — and
 * it breaks silently, which is the worst kind, because support looks staffed
 * and nobody is receiving anything.
 *
 * So the socket is an ACCELERATOR, never the only path. LiveChat polls
 * slowly while connected and quickly while not, and every message arrives by
 * whichever route gets there first; they are deduplicated by id, so a message
 * delivered twice is rendered once.
 *
 * ── AND WHY THIS IS LAZY ────────────────────────────────────────────────────
 *
 * Nothing connects until something subscribes. The shop, the checkout and the
 * blog never open a socket, so the only people holding one are the ones with
 * the support page in front of them.
 */

type EchoClient = InstanceType<typeof Echo>;

let echo: EchoClient | null = null;
let failed = false;

/**
 * The enquiry token the support widget is currently using, if any.
 *
 * ── WHY THE SOCKET NEEDS TO KNOW ────────────────────────────────────────────
 *
 * Laravel's /broadcasting/auth sits behind `auth:sanctum`, and a widget
 * visitor has no bearer token — that is the whole design. Left alone, every
 * subscription they attempted was refused, the chat fell back to polling, and
 * it all looked like it was working while the socket did nothing.
 *
 * So when the widget is driving, Echo authorises through the enquiry's own
 * endpoint, which signs exactly one channel and refuses the rest.
 */
let enquiryToken: string | null = null;

/**
 * Point the socket at an enquiry. Rebuilds the client if it changes.
 *
 * Named `set`, not `use`: it is a plain setter on module state, and a `use`
 * prefix makes every linter and every reader treat it as a React hook.
 */
export function setEnquiryToken(token: string | null): void {
  if (token === enquiryToken) return;

  enquiryToken = token;

  // The authoriser was baked in when the client was built, so it has to go.
  disconnectRealtime();
}

/** Whether a socket is even configured. An empty key is a supported state. */
export function realtimeConfigured(): boolean {
  return Boolean((import.meta.env.VITE_REVERB_KEY ?? '').trim());
}

/**
 * The shared Echo client, created on first use.
 *
 * Returns null when there is no key, or when a previous attempt threw — a
 * misconfigured socket must never take the page down with it, because the
 * polling path is right there and works.
 */
export function realtime(): EchoClient | null {
  if (echo || failed) return echo;

  if (!realtimeConfigured()) return null;

  try {
    // laravel-echo looks for this on window rather than importing it.
    (window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;

    echo = new Echo({
      broadcaster: 'reverb',
      key: import.meta.env.VITE_REVERB_KEY,
      wsHost: import.meta.env.VITE_REVERB_HOST ?? window.location.hostname,
      wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
      wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 443),
      forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'http') === 'https',
      enabledTransports: ['ws', 'wss'],

      // Private channels are authorised against the API, on the SANCTUM
      // guard, with the same bearer token every other request carries. There
      // is no session and no CSRF cookie in this app.
      ...(enquiryToken
        ? {
            // A visitor with no account. Their enquiry token signs the one
            // channel it already reaches, and the server checks the channel
            // name against THEIR thread before issuing anything.
            authorizer: (channel: { name: string }) => ({
              authorize: (
                socketId: string,
                // Pusher's own signature: an Error, or null for success.
                callback: (error: Error | null, data: { auth: string } | null) => void,
              ) => {
                fetch(`${import.meta.env.VITE_API_URL ?? ''}/support/enquiry/socket-auth`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Support-Token': enquiryToken ?? '',
                  },
                  body: JSON.stringify({
                    socket_id: socketId,
                    channel_name: channel.name,
                  }),
                })
                  .then((response) =>
                    response.ok ? response.json() : Promise.reject(new Error('refused')),
                  )
                  .then((payload) => callback(null, payload.data))
                  // Refused is not fatal: the chat carries on polling, which
                  // is the behaviour this fallback exists to guarantee.
                  .catch((error: Error) => callback(error, null));
              },
            }),
          }
        : {
            authEndpoint: `${import.meta.env.VITE_API_URL ?? ''}/broadcasting/auth`,
            auth: {
              headers: {
                Authorization: `Bearer ${tokenStore.get() ?? ''}`,
                Accept: 'application/json',
              },
            },
          }),
    });

    return echo;
  } catch {
    // One attempt. Retrying a broken configuration on every render would be a
    // connection storm in exchange for nothing.
    failed = true;
    echo = null;

    return null;
  }
}

/** True once the socket is actually carrying traffic, not merely configured. */
export function realtimeConnected(): boolean {
  const client = realtime();

  if (!client) return false;

  try {
    const connector = client.connector as { pusher?: { connection?: { state?: string } } };

    return connector.pusher?.connection?.state === 'connected';
  } catch {
    return false;
  }
}

/**
 * Drop the socket.
 *
 * Called on sign-out: the auth header was baked in when the client was built,
 * so a stale Echo would keep trying to authorise channels as somebody who has
 * left.
 */
export function disconnectRealtime(): void {
  try {
    echo?.disconnect();
  } catch {
    /* Already gone. */
  }

  echo = null;
  failed = false;
}
